import { useNavigate } from 'react-router-dom';
import { Card, message, theme } from 'antd';
import { Permission } from '@aid/shared';
import { EmptyState } from '../../components/EmptyState';
import { AdminBreadcrumbs } from '../../components/AdminBreadcrumbs';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { useCreateUser, type CreateUserInput } from './api/useUsers';
import { UserForm, type UserFormValues } from './components/UserForm';

export function UserCreatePage() {
  const t = useT();
  const navigate = useNavigate();
  const createUser = useCreateUser();
  const hasPermission = useHasPermission(Permission.USERS_CREATE);
  const { token } = theme.useToken();

  if (!hasPermission) {
    return <EmptyState title={t('pages.users.noCreatePermission')} />;
  }

  const handleSubmit = async (values: UserFormValues) => {
    if (!values.email || !values.username || !values.password) return;

    const input: CreateUserInput = {
      email: values.email,
      username: values.username,
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      jobTitle: values.jobTitle,
      department: values.department,
      isActive: values.isActive,
      roleIds: values.roleIds,
    };

    try {
      await createUser.mutateAsync(input);
      message.success(t('pages.users.createSuccess'));
      navigate('/users');
    } catch {
      message.error(t('pages.users.createError'));
    }
  };

  return (
    <div>
      <PageHeader
        breadcrumb={<AdminBreadcrumbs current={t('pages.users.create')} />}
        title={t('pages.users.create')}
        subtitle={t('pages.users.createSubtitle')}
      />
      <Card
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
      >
        <UserForm
          mode="create"
          onSubmit={handleSubmit}
          loading={createUser.isPending}
          onCancel={() => navigate('/users')}
        />
      </Card>
    </div>
  );
}
